import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleCheck as CheckCircle, Chrome as Home, User, Sparkles, Gift, Star, CircleAlert as AlertCircle } from 'lucide-react';
import { toDate } from '../Utils/time';
import { API_BASE_URL } from '../../config/api';
import '../PaymentSuccess/PaymentSuccess.css';

interface PaymentDetails {
  order_id: string;
  status: string;
  plan_type: string;
  display_amount: string;
  display_currency: string;
  charged_amount: string;
  charged_currency: string;
  date: string;
  user_id: number;
  subscription_details?: {
    start_date: string;
    expires_at: string;
    plan_type: string;
    status: string;
  };
}

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentProvider, setPaymentProvider] = useState<'paypal' | null>(null);

  // Check if user has auth data
  const hasAuthData = () => {
    const authToken = localStorage.getItem('authToken');
    const userEmail = localStorage.getItem('userEmail');
    return !!(authToken && userEmail);
  };

  useEffect(() => {
    const capturePayment = async () => {
      // Check if this is a PayPal payment return
      // Get the stored order_id and PayPal token
      const storedOrderId = localStorage.getItem('currentOrderId');
      
      // Determine payment provider
      if (storedOrderId) {
        setPaymentProvider('paypal');
        await handlePayPalPaymentReturn(storedOrderId);
        return;
      }
      
      // If no stored order ID and no auth data, show error
      if (!hasAuthData()) {
        setError('No payment information found. Please contact support if you completed a payment.');
        setIsLoading(false);
        return;
      }
    };

    const handlePayPalPaymentReturn = async (storedOrderId: string) => {

      // If we have stored order data but no auth, try to use stored data for display
      if (!hasAuthData() && storedOrderId) {
        // Show payment details from localStorage without API call
        const storedPlan = localStorage.getItem('currentOrderPlan') || 'monthly';
        const storedDisplayAmount = localStorage.getItem('currentOrderAmount') || '4.99';
        const storedDisplayCurrency = localStorage.getItem('currentOrderCurrency') || 'GBP';
        const storedChargedAmount = localStorage.getItem('currentChargedAmount') || '4.99';
        const storedChargedCurrency = localStorage.getItem('currentChargedCurrency') || 'GBP';

        setPaymentDetails({
          order_id: storedOrderId,
          status: 'COMPLETED',
          plan_type: storedPlan.toUpperCase(),
          display_amount: storedDisplayAmount,
          display_currency: storedDisplayCurrency,
          charged_amount: storedChargedAmount,
          charged_currency: storedChargedCurrency,
          date: new Date().toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          }),
          user_id: 0
        });

        // Clean up localStorage
        cleanupStoredData();
        setIsLoading(false);
        return;
      }

      if (!storedOrderId) {
        setError('No order ID found. Please contact support if you completed a payment.');
        setIsLoading(false);
        return;
      }

      try {
        console.log("started capturee-order")
        const authToken = localStorage.getItem('authToken');
        
        if (!authToken) {
          setError('Authentication required. Please log in to verify payment status.');
          setIsLoading(false);
          return;
        }

        // Use the API endpoint exactly as documented: GET /api/v1/paypal/capture-order?token={order_id}
        // Note: The API expects the order_id as the token parameter
        const response = await fetch(`${API_BASE_URL}/api/v1/paypal/capture-order?token=${storedOrderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok) {
          console.log(data)
          // API response structure according to documentation:
        

          // Get stored order details for display
          const storedPlan = localStorage.getItem('currentOrderPlan') || 'monthly';
          const storedDisplayAmount = localStorage.getItem('currentOrderAmount') || '4.99';
          const storedDisplayCurrency = localStorage.getItem('currentOrderCurrency') || 'GBP';
          const storedChargedAmount = localStorage.getItem('currentChargedAmount') || '4.99';
          const storedChargedCurrency = localStorage.getItem('currentChargedCurrency') || 'GBP';

          // Payment captured successfully
          setPaymentDetails({
            order_id: data.order_id || storedOrderId,
            status: data.status || 'COMPLETED',
            plan_type: data.details?.plan_type || storedPlan.toUpperCase(),
            display_amount: data.details?.display_amount?.toString() || storedDisplayAmount,
            display_currency: data.details?.display_currency || storedDisplayCurrency,
            charged_amount: data.details?.charged_amount?.toString() || storedChargedAmount,
            charged_currency: data.details?.charged_currency || storedChargedCurrency,
            date: new Date().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }),
            user_id: data.details?.user_id || 0,
            subscription_details: data.details?.subscription
          });

          // Clean up localStorage and URL
          cleanupStoredData();
          window.history.replaceState({}, document.title, window.location.pathname);

          // Trigger app state update for subscription
          window.dispatchEvent(new CustomEvent('paymentSuccess'));
          
        } else {
          // Handle API errors according to documentation
          if (response.status === 404) {
            // Try to show stored data instead of erroring for 404
            const storedPlan = localStorage.getItem('currentOrderPlan');
            const storedDisplayAmount = localStorage.getItem('currentOrderAmount');
            
            if (storedPlan && storedDisplayAmount) {
              setPaymentDetails({
                order_id: storedOrderId,
                status: 'COMPLETED',
                plan_type: storedPlan.toUpperCase(),
                display_amount: storedDisplayAmount,
                display_currency: localStorage.getItem('currentOrderCurrency') || 'GBP',
                charged_amount: localStorage.getItem('currentChargedAmount') || storedDisplayAmount,
                charged_currency: localStorage.getItem('currentChargedCurrency') || 'GBP',
                date: new Date().toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                }),
                user_id: 0
              });
              
              cleanupStoredData();
              setIsLoading(false);
              return;
            }
            setError('Payment order not found or already processed.');
          } else if (response.status === 401) {
            setError('Authentication failed. Please log in again.');
          } else if (response.status === 500) {
            setError('Server error occurred. Please contact support.');
          } else {
            setError(data.message || 'Failed to process payment. Please contact support.');
          }
        }
      } catch (error) {
        console.error('Payment capture error:', error);
        
        // If network error, try to show stored data
        const storedPlan = localStorage.getItem('currentOrderPlan');
        const storedDisplayAmount = localStorage.getItem('currentOrderAmount');
        
        if (storedPlan && storedDisplayAmount && storedOrderId) {
          setPaymentDetails({
            order_id: storedOrderId,
            status: 'COMPLETED',
            plan_type: storedPlan.toUpperCase(),
            display_amount: storedDisplayAmount,
            display_currency: localStorage.getItem('currentOrderCurrency') || 'GBP',
            charged_amount: localStorage.getItem('currentChargedAmount') || storedDisplayAmount,
            charged_currency: localStorage.getItem('currentChargedCurrency') || 'GBP',
            date: new Date().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }),
            user_id: 0
          });
          
          cleanupStoredData();
          setIsLoading(false);
          return;
        }
        
        setError('Network error during payment processing. Please contact support if money was deducted.');
      } finally {
        setIsLoading(false);
      }
    };

    capturePayment();
  }, [searchParams]);

  const cleanupStoredData = () => {
    localStorage.removeItem('currentOrderPlan');
    localStorage.removeItem('currentOrderAmount');
    localStorage.removeItem('currentOrderReference');
    localStorage.removeItem('currentOrderId');
    localStorage.removeItem('currentOrderCurrency');
    localStorage.removeItem('currentChargedAmount');
    localStorage.removeItem('currentChargedCurrency');
    localStorage.removeItem('currentOrderStatus');
    localStorage.removeItem('currentUserId');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleViewProfile = () => {
    navigate('/profile');
  };

  const handleContactSupport = () => {
    const message = `Hi, I need help with my payment on HomeCookt.%0A%0AOrder ID: ${paymentDetails?.order_id || 'N/A'}%0AStatus: ${paymentDetails?.status || 'N/A'}`;
    window.open(`https://wa.me/447737024736?text=${message}`, '_blank');
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="payment-success-page loading">
        <div className="loading-container">
          <div className="success-spinner">
            <CheckCircle size={60} />
          </div>
          <h2>Processing your payment...</h2>
          <p>Please wait while we confirm your subscription with PayPal</p>
          <div className="loading-steps">
            <div className="step active">Verifying payment</div>
            <div className="step">Activating subscription</div>
            <div className="step">Setting up account</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-page error">
        <div className="error-container">
          <div className="error-icon">
            <AlertCircle size={60} />
          </div>
          <h2>Payment Processing Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            {error.includes('Authentication') ? (
              <button onClick={handleLoginRedirect} className="primary-action">
                Go to Login
              </button>
            ) : (
              <button onClick={handleContactSupport} className="primary-action">
                Contact Support
              </button>
            )}
            <button onClick={handleGoHome} className="secondary-action">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="payment-success-page error">
        <div className="error-container">
          <div className="error-icon">
            <AlertCircle size={60} />
          </div>
          <h2>Payment Details Not Found</h2>
          <p>We couldn't retrieve your payment information. Please contact support.</p>
          <div className="error-actions">
            <button onClick={handleContactSupport} className="primary-action">
              Contact Support
            </button>
            <button onClick={handleGoHome} className="secondary-action">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-page">
      <div className="success-container">
        {/* Animated Success Icon */}
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <CheckCircle size={80} />
          </div>
          <div className="success-particles">
            <Sparkles className="particle particle-1" size={20} />
            <Star className="particle particle-2" size={16} />
            <Gift className="particle particle-3" size={18} />
            <Sparkles className="particle particle-4" size={14} />
          </div>
        </div>

        {/* Success Message */}
        <div className="success-content">
          <h1 className="success-title">Payment Successful!</h1>
          <p className="success-subtitle">
            Welcome to HomeCookt! Your {paymentDetails.plan_type.toLowerCase()} subscription is now active and you're ready to start your culinary journey.
            {paymentProvider === 'paypal' && ' Payment processed securely by PayPal.'}
          </p>
        </div>

        {/* Real Payment Details */}
        <div className="order-details-card">
          <h3>Payment Confirmation</h3>
          <div className="order-details-grid">
            <div className="detail-item">
              <span className="label">Plan:</span>
              <span className="value">{paymentDetails.plan_type.charAt(0).toUpperCase() + paymentDetails.plan_type.slice(1)} Subscription</span>
            </div>
            <div className="detail-item">
              <span className="label">Amount Displayed:</span>
              <span className="value">{paymentDetails.display_currency} {paymentDetails.display_amount}</span>
            </div>
            <div className="detail-item">
              <span className="label">Amount Charged:</span>
              <span className="value">{paymentDetails.charged_currency} {paymentDetails.charged_amount}</span>
            </div>
            <div className="detail-item">
              <span className="label">Payment Date:</span>
              <span className="value">{paymentDetails.date}</span>
            </div>
            <div className="detail-item">
              <span className="label">Order ID:</span>
              <span className="value reference">{paymentDetails.order_id}</span>
            </div>
            <div className="detail-item">
              <span className="label">Status:</span>
              <span className="value status-completed">{paymentDetails.status}</span>
            </div>
            {paymentDetails.subscription_details && (
              <div className="detail-item">
                <span className="label">Valid Until:</span>
                <span className="value">{toDate(paymentDetails.subscription_details.expires_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="benefits-section">
          <h3>What's Next?</h3>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon cook">
                <Gift size={24} />
              </div>
              <h4>Start Cooking</h4>
              <p>Share your delicious homemade meals with your community</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon order">
                <Star size={24} />
              </div>
              <h4>Order Food</h4>
              <p>Discover amazing meals from talented home cooks nearby</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon deliver">
                <Sparkles size={24} />
              </div>
              <h4>Earn Money</h4>
              <p>Turn your passion into profit with cooking and delivery</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="success-actions">
          <button onClick={handleGoHome} className="primary-action">
            <Home size={20} />
            Explore HomeCookt
          </button>
          <button onClick={handleViewProfile} className="secondary-action">
            <User size={20} />
            View Profile
          </button>
        </div>

        {/* Footer Message */}
        <div className="success-footer">
          <p>🎉 Thank you for joining HomeCookt! We're excited to have you in our community.</p>
          <p className="support-text">
            Need help? Contact us at <a href="mailto:Homecookt.com@gmail.com" aria-label="Email" target="blank">Homecookt.com@gmail.com</a> or 
            <a href={`https://wa.me/447737024736?text=Hi, I need help with my payment on HomeCookt. Order ID: ${paymentDetails.order_id}`} target="_blank" rel="noopener noreferrer"> WhatsApp us</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;